/*
 * Consulting — /consulting
 *
 * Modernised to match the site's navy + amber design language (Pricing,
 * BusinessCalc, UserPortal). Replaces the emerald-green legacy layout
 * that predated the brand refresh.
 */
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, CheckCircle2, Headphones, Sparkles, TrendingUp, Megaphone,
  Clock, ShieldCheck, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const PILLARS = [
  { icon: Sparkles,   title: 'Recipe development',  body: 'Dial in cycle, moisture and texture for a specific SKU — including pet treats, meals-ready-to-eat, and premium retail.' },
  { icon: TrendingUp, title: 'Business strategy',   body: 'Pricing, positioning and margin structure. Right-size batch cadence, staffing and equipment for the market you\'re actually in.' },
  { icon: Megaphone,  title: 'Marketing & launch',  body: 'Brand story, farmers-market playbook, Shopify funnel and creator partnerships that convert without paid-ads waste.' },
];

// Roster of operators who handle inbound requests. Names + photos are
// deliberately hand-curated so leads see a real face before they book.
// Photos: 128×128 crops of the Freeze Dry Academy team.
const CONSULTANTS = [
  {
    name: 'Kaitlyn R.',
    role: 'Lead consultant · Recipe & retail',
    specialty: 'Pet treats, dessert bars, retail-ready SKUs',
    photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=256&h=256&fit=crop&auto=format',
    tags: ['Recipes', 'Pouching', 'Farmers markets'],
  },
  {
    name: 'Marcus D.',
    role: 'Consultant · Business & scale',
    specialty: 'Pricing, cycle economics, capital planning',
    photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=256&h=256&fit=crop&auto=format',
    tags: ['Margins', 'Capex', 'Scaling'],
  },
  {
    name: 'Sarah L.',
    role: 'Consultant · Marketing & brand',
    specialty: 'Shopify, creator partnerships, storytelling',
    photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=256&h=256&fit=crop&auto=format',
    tags: ['DTC', 'Branding', 'Creators'],
  },
];

const TRUST = [
  { icon: Clock,        label: 'Reply in 1-2 business days' },
  { icon: ShieldCheck,  label: 'NDA available on request' },
  { icon: MessageSquare, label: 'First 20 min discovery call is free' },
];

const Consulting = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ topic: '', preferred_times: '', contact_info: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.topic || !formData.preferred_times || !formData.contact_info) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consulting`, formData);
      setSubmitted(true);
      toast.success('Request submitted');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="consulting-page">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto px-6 h-14 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="-ml-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
          <span className="text-sm font-bold text-foreground flex items-center gap-1.5">
            <Headphones className="w-4 h-4 text-primary" /> Consulting
          </span>
        </div>
      </header>

      <main className="container mx-auto px-6 py-10 max-w-5xl">
        {submitted ? (
          <div className="pro-card p-10 text-center max-w-2xl mx-auto" data-testid="consulting-submitted">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/40 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <span className="eyebrow inline-block mt-4">Request received</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
              We'll be in touch shortly.
            </h1>
            <p className="text-muted-foreground mt-3">
              A member of the Freeze Dry Academy consulting team will review your brief and reach out
              within <b>1-2 business days</b> to schedule your discovery call.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Button onClick={() => navigate('/dashboard')} className="rounded-full font-bold px-6" data-testid="back-to-dashboard-btn">
                Back to dashboard
              </Button>
              <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({ topic: '', preferred_times: '', contact_info: '' }); }} className="rounded-full font-bold px-6" data-testid="new-request-btn">
                Submit another request
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* HERO */}
            <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-start">
              <div>
                <span className="eyebrow inline-flex items-center gap-1.5" data-testid="consulting-eyebrow">
                  <Sparkles className="w-3.5 h-3.5" /> 1:1 Consulting
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2 leading-[1.05]">
                  Get an operator in your corner — <span className="text-amber-brand">not</span> another course.
                </h1>
                <p className="text-muted-foreground mt-4 max-w-xl">
                  Book time with a working freeze-dry operator to pressure-test your recipe, price, or launch plan.
                  Sessions are private, focused, and shaped around whatever's blocking you this month.
                </p>
                <div className="mt-6 flex flex-wrap gap-2" data-testid="consulting-trust">
                  {TRUST.map((t) => (
                    <div
                      key={t.label}
                      className="inline-flex items-center gap-1.5 rounded-full bg-card border border-border px-3 py-1.5 text-[11px] font-bold text-foreground"
                    >
                      <t.icon className="w-3.5 h-3.5 text-primary" /> {t.label}
                    </div>
                  ))}
                </div>
                <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="consulting-pillars">
                  {PILLARS.map((p) => (
                    <div
                      key={p.title}
                      className="pro-card p-4 hover:border-primary/50 transition-colors"
                      data-testid={`pillar-${p.title.split(' ')[0].toLowerCase()}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                        <p.icon className="w-4 h-4 text-primary" />
                      </div>
                      <div className="mt-3 font-heading text-sm font-extrabold tracking-tight">{p.title}</div>
                      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{p.body}</p>
                    </div>
                  ))}
                </div>

                {/* Consultant roster — real faces so leads know who's on the
                    other side of the booking form before they submit. */}
                <div className="mt-8" data-testid="consulting-roster">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Who you'll be talking to
                  </div>
                  <h3 className="font-heading text-xl font-extrabold tracking-tight mt-1">
                    Working operators — not career course-sellers.
                  </h3>
                  <div className="mt-3 grid sm:grid-cols-3 gap-3">
                    {CONSULTANTS.map((c) => (
                      <div
                        key={c.name}
                        className="pro-card p-4 flex flex-col"
                        data-testid={`consultant-${c.name.split(' ')[0].toLowerCase()}`}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={c.photo}
                            alt={c.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-border shrink-0"
                            loading="lazy"
                          />
                          <div className="min-w-0">
                            <div className="font-heading text-sm font-extrabold tracking-tight truncate">{c.name}</div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-primary truncate">{c.role}</div>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{c.specialty}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {c.tags.map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center rounded-full bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-500/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* FORM */}
              <div className="pro-card p-6 lg:p-7 lg:sticky lg:top-20" data-testid="consulting-form-card">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  Request a session
                </div>
                <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-1">
                  Tell us what you're solving.
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  The more specific the topic, the better the match — we route to the operator with the closest lane.
                </p>
                <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                  <div>
                    <Label htmlFor="topic" className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                      Topic / area of interest
                    </Label>
                    <Input
                      id="topic"
                      value={formData.topic}
                      onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                      placeholder="e.g., Cycle tuning for freeze-dried pet treats"
                      className="h-11 mt-1.5"
                      data-testid="topic-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="times" className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                      Preferred times / availability
                    </Label>
                    <Textarea
                      id="times"
                      value={formData.preferred_times}
                      onChange={(e) => setFormData({ ...formData, preferred_times: e.target.value })}
                      placeholder="e.g., Weekday mornings PT, or two 30-min blocks this Friday"
                      rows={3}
                      className="mt-1.5"
                      data-testid="times-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact" className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                      Contact information
                    </Label>
                    <Textarea
                      id="contact"
                      value={formData.contact_info}
                      onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                      placeholder="Email + phone/preferred channel"
                      rows={2}
                      className="mt-1.5"
                      data-testid="contact-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 rounded-full font-bold"
                    data-testid="submit-consulting-btn"
                  >
                    {loading ? 'Submitting…' : 'Submit request'}
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">
                    By submitting, you agree to a follow-up email or call from the FDA consulting team.
                  </p>
                </form>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Consulting;
